import React, { useMemo, useState } from "react";
import { Alert, Button, Modal, Select, Tooltip, Typography } from "antd";
import { DollarOutlined } from "@ant-design/icons";
import PermissionGuard from "../../../../../components/auth/PermissionGuard";
import { CURRENCY_SYMBOLS, WRITABLE_CURRENCY_CODES } from "../../../constants";

const { Text } = Typography;

// These states have a completed structure payload (or a later authoritative AAI)
// that can safely receive a settlement_currency override.
export const SETTLEMENT_CURRENCY_EDITABLE_STATUSES = new Set([
    "STRUCTURE_COMPLETED",
    "STRUCTURE_BLOCKED",
    "ALLOCATION_IN_PROGRESS",
    "ALLOCATION_COMPLETED",
    "ALLOCATION_FAILED",
    "ALLOCATION_BLOCKED",
    "VALIDATION_IN_PROGRESS",
    "VALIDATION_COMPLETED",
    "BATCH_VALIDATION_IN_PROGRESS",
    "BATCH_VALIDATION_COMPLETED",
    "BATCH_VALIDATION_FAILED",
]);

const CURRENCY_OPTIONS = WRITABLE_CURRENCY_CODES.map((currency) => ({
    value: currency,
    label: `${currency} (${CURRENCY_SYMBOLS[currency]})`,
}));

export const splitSettlementCurrencySelection = (selectedRowKeys = [], records = []) => {
    const recordById = new Map(records.map((record) => [String(record.id), record]));
    const eligibleIds = [];
    const ineligibleRecords = [];

    selectedRowKeys.forEach((rowKey) => {
        const record = recordById.get(String(rowKey));
        if (record && SETTLEMENT_CURRENCY_EDITABLE_STATUSES.has(record.status)) {
            eligibleIds.push(String(record.id));
            return;
        }
        ineligibleRecords.push(record || { id: rowKey, status: null });
    });

    return { eligibleIds, ineligibleRecords };
};

const BatchSettlementCurrencyAction = ({ selectedRowKeys, records, onSubmit, onCompleted }) => {
    const [open, setOpen] = useState(false);
    const [selectedCurrency, setSelectedCurrency] = useState(undefined);
    const [submitting, setSubmitting] = useState(false);
    const { eligibleIds, ineligibleRecords } = useMemo(
        () => splitSettlementCurrencySelection(selectedRowKeys, records),
        [selectedRowKeys, records],
    );

    const closeModal = () => {
        setOpen(false);
        setSelectedCurrency(undefined);
    };

    const handleSubmit = async () => {
        if (!selectedCurrency || eligibleIds.length === 0) return;
        setSubmitting(true);
        try {
            const result = await onSubmit(eligibleIds, selectedCurrency);
            const succeeded = Number(result?.data?.succeeded ?? (result?.success ? eligibleIds.length : 0));
            if (succeeded <= 0) return;
            closeModal();
            onCompleted?.(result);
        } catch {
            // The context layer already shows the operator-facing error. Keep the
            // modal open so the selection and chosen currency are not lost.
        } finally {
            setSubmitting(false);
        }
    };

    const button = (
        <Button
            type="primary"
            size="small"
            ghost
            icon={<DollarOutlined />}
            aria-label={`批量设置币种 (${eligibleIds.length}/${selectedRowKeys.length})`}
            disabled={eligibleIds.length === 0}
            onClick={() => setOpen(true)}
            style={{ fontSize: "12px" }}
        >
            批量设置币种 ({eligibleIds.length}/{selectedRowKeys.length})
        </Button>
    );

    return (
        <PermissionGuard permission="settlement_ap_reconciliation:update">
            {eligibleIds.length === 0 ? (
                <Tooltip title="所选账单尚未生成可修改的账单明细">
                    <span>{button}</span>
                </Tooltip>
            ) : button}

            <Modal
                title="批量设置结算币种"
                open={open}
                onCancel={closeModal}
                onOk={handleSubmit}
                okText="确认设置"
                cancelText="取消"
                width={440}
                okButtonProps={{ disabled: !selectedCurrency, loading: submitting }}
                maskClosable={!submitting}
                closable={!submitting}
            >
                <div style={{ marginBottom: 16 }}>
                    <Text>
                        已选择 <Text strong>{selectedRowKeys.length}</Text> 张账单，将为其中可修改的
                        {" "}<Text strong>{eligibleIds.length}</Text> 张设置结算币种。
                    </Text>
                </div>

                {ineligibleRecords.length > 0 && (
                    <Alert
                        type="warning"
                        showIcon
                        message={`${ineligibleRecords.length} 张账单尚未生成可修改明细，将自动跳过`}
                        style={{ marginBottom: 16 }}
                    />
                )}

                <Text style={{ display: "block", marginBottom: 8 }}>结算币种</Text>
                <Select
                    value={selectedCurrency}
                    onChange={setSelectedCurrency}
                    options={CURRENCY_OPTIONS}
                    placeholder="请选择结算币种"
                    style={{ width: "100%" }}
                    showSearch
                    optionFilterProp="label"
                />
            </Modal>
        </PermissionGuard>
    );
};

export default BatchSettlementCurrencyAction;
